import { isNotPrimitive } from '@repo/alt/common/Utils/Data.js'

import { useConfirm } from './useConfirm'

import {
  ApiValidationError,
  checkIsApiError,
  type RawApiErrors,
} from '#Common/models/Error'

type Config = {
  key?: string // уникальный ключ отправляемого запроса
  silent?: boolean // показывать ли уведомление с текстом ошибки пользователю
  collectApiErrors?: boolean // выбрасывать ли полученную ошибку наружу
  throwError?: boolean // выбрасывать ли полученную ошибку наружу
  successMessage?: string
  confirm?: {
    message?: string
    accept?: string
    reject?: string
  }
}
type PendingRequests = Map<string | symbol, Promise<unknown>>

const TOAST_DURATION = 6000
const CUSTOM_LOADING_KEY = Symbol('custom-loading')

function extractApiError(error: unknown): RawApiErrors | undefined {
  if (
    isNotPrimitive(error)
    && 'response' in error
    && isNotPrimitive(error.response)
    && 'data' in error.response
    && error.response.data
  ) {
    try {
      const isApiError = checkIsApiError(error.response.data)
      return isApiError ? (error.response.data as RawApiErrors) : undefined
    } catch (error) {
      return undefined
    }
  } else if (
    isNotPrimitive(error)
    && 'response' in error
    && checkIsApiError(error.response)
  ) {
    return error.response as RawApiErrors
  } else if (checkIsApiError(error)) {
    return error as RawApiErrors
  }
}

export const useRequest = (scope?: string) => {
  const toast = useToast()
  const confirm = useConfirm()

  const loadingStrategies = new Map<boolean, Ref<PendingRequests>>([
    [false, ref<PendingRequests>(new Map())],
    [
      true,
      useState<PendingRequests>(`pending-requests-${scope}`, () => new Map()),
    ],
  ])

  // Мапа отправляемых запросов
  const pendingRequests = loadingStrategies.get(!!scope) as Ref<PendingRequests>
  const errors = ref<Record<string | 'undefined', RawApiErrors | undefined>>({})

  // true, если хотя бы 1 запрос отправляется
  const isLoading = computed<boolean>(() => pendingRequests.value.size > 0)

  // Если необходимо вручную поставить флаг isLoading в true, даже если никаких запросов сейчас не отправляется
  function setLoading(loading: boolean) {
    if (loading) {
      pendingRequests.value.set(CUSTOM_LOADING_KEY, Promise.resolve())
    } else {
      pendingRequests.value.delete(CUSTOM_LOADING_KEY)
    }
  }

  // Проверка конкретного запроса отправляется ли он в данный момент
  function checkIsLoading(key: string) {
    return Boolean(pendingRequests.value.get(key))
  }

  async function send<T>(
    sender: () => Promise<T>,
    config: Config & { throwError: true },
  ): Promise<T>
  async function send<T>(
    sender: () => Promise<T>,
    config?: Omit<Config, 'throwError'> & { throwError?: false },
  ): Promise<T | undefined>
  async function send<T>(
    sender: () => Promise<T>,
    config?: Config,
  ): Promise<T | undefined> {
    const requestKey = (config?.key || scope) as string
    const silent = config?.silent || false
    const throwError = Boolean(config?.throwError)

    if (checkIsLoading(requestKey)) {
      return pendingRequests.value.get(requestKey) as Promise<T>
    }
    const request = async () => {
      try {
        pendingRequests.value.set(requestKey, sender())
        const response = (await pendingRequests.value.get(requestKey)) as T

        config?.successMessage
        && toast.add({
          color: 'success',
          description: config.successMessage,
        })

        return response
      } catch (e: any) {
        const apiError = extractApiError(e)
        if (apiError && config?.collectApiErrors) {
          errors.value[String(requestKey)] = apiError
        }

        if (!silent && apiError) {
          showErrorToasts(apiError)
        }

        function showErrorToasts(
          error: RawApiErrors,
          fallbackMessage?: string,
        ) {
          const message
            = ApiValidationError.getMessages(error)
              || error.detail
              || error.title
              || fallbackMessage
              || 'Unknown error'
          const messages = ([] as string[]).concat(message)

          messages.forEach((message) => {
            toast.add({
              color: 'error',
              title: 'Ошибка',
              description: message || fallbackMessage || 'Неизвестная ошибка',
            })
          })
        }

        if (throwError) {
          throw e
        }
      } finally {
        pendingRequests.value.delete(requestKey)
      }
    }

    if (config?.confirm) {
      const confirmed = await confirm.require({
        header: 'Подтверждение',
        message: config.confirm.message,
        acceptProps: {
          label: config.confirm.accept || 'Подтвердить',
        },
        rejectProps: {
          label: config.confirm.reject || 'Отмена',
        },
      })

      if (confirmed) {
        return request()
      }
      return undefined
    } else {
      return request()
    }
  }

  function getError(key?: string) {
    return errors.value[String(key || scope)]
  }

  function clearError(key?: string) {
    return (errors.value[String(key || scope)] = undefined)
  }

  return {
    send,
    isLoading,
    getError,
    clearError,
    setLoading,
    checkIsLoading,
  }
}
